import React, { useState } from 'react';
import {
  TextInput,
  SelectInput,
  BooleanInput,
  DateInput,
  ReferenceInput,
  AutocompleteInput,
  required,
  email,
  useRecordContext,
  useInput,
} from 'react-admin';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Description as NotesIcon,
  Badge as BadgeIcon,
  TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { LocationDialog } from '../../components/LocationDialog';

interface LeadFormProps {
  isEdit?: boolean;
}

const statusChoices = [
  { id: 'new', name: 'New' },
  { id: 'contacted', name: 'Contacted' },
  { id: 'qualified', name: 'Qualified' },
  { id: 'converted', name: 'Converted' },
  { id: 'lost', name: 'Lost' },
];

const sourceChoices = [
  { id: 'Website', name: 'Website' },
  { id: 'SocialMedia', name: 'Social Media' },
  { id: 'Referral', name: 'Referral' },
  { id: 'Advertisement', name: 'Advertisement' },
  { id: 'Event', name: 'Event' },
  { id: 'ColdCall', name: 'Cold Call' },
  { id: 'Other', name: 'Other' },
];

const companySizeChoices = [
  { id: '1-10', name: '1-10 employees' },
  { id: '11-50', name: '11-50 employees' },
  { id: '51-200', name: '51-200 employees' },
  { id: '201-500', name: '201-500 employees' },
  { id: '501-1000', name: '501-1000 employees' },
  { id: '1000+', name: '1000+ employees' },
];



export const LeadForm: React.FC<LeadFormProps> = ({ isEdit = false }) => {
  const record = useRecordContext();
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(record?.location || null);
  
  // Use hidden input to manage locationId
  const locationIdInput = useInput({ source: 'locationId' });
  
  const handleLocationCreated = (location: any) => {
    setSelectedLocation(location);
    locationIdInput.field.onChange(location.id);
    setLocationDialogOpen(false);
  };

  return (
    <Grid container spacing={3}>
      {/* Lead Identifier - First and Separated */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: 'primary.main',
                fontWeight: 'bold',
              }}
            >
              <BadgeIcon color="primary" />
              Lead Identifier
            </Typography>
            <TextInput
              source="displayName"
              label="Display Name"
              validate={[required('Display name is required')]}
              fullWidth
              helperText="A unique name to identify this lead (e.g., 'John Smith - ABC Corp' or 'Jane Doe')"
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Lead Details
          </Typography>
        </Divider>
      </Grid>

      {/* Contact Information */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              Contact Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextInput source="firstName" label="First Name" fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput source="lastName" label="Last Name" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextInput source="title" label="Title" fullWidth helperText="Job title or position" />
              </Grid>
              <Grid item xs={12}>
                <TextInput source="email" label="Email" validate={[email()]} fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextInput source="phone" label="Phone" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextInput source="company" label="Company" fullWidth />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Lead Status & Details */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon color="primary" />
              Lead Status & Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SelectInput 
                  source="status" 
                  label="Status"
                  choices={statusChoices}
                  defaultValue="new"
                  validate={required('Status is required')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <SelectInput 
                  source="source" 
                  label="Lead Source"
                  choices={sourceChoices}
                  defaultValue="Website"
                  validate={required('Lead source is required')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <BooleanInput source="qualified" label="Qualified Lead" />
              </Grid>
              <Grid item xs={12}>
                <DateInput source="lastContactDate" label="Last Contact Date" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <ReferenceInput
                  source="customerTypeId"
                  reference="crm/customer-types"
                  label="Customer Type"
                  fullWidth
                >
                  <AutocompleteInput optionText="name" />
                </ReferenceInput>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Company Details */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon color="primary" />
              Company Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextInput source="industry" label="Industry" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <SelectInput 
                  source="companySize" 
                  label="Company Size"
                  choices={companySizeChoices}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput source="budget" label="Budget" fullWidth />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Location Information */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              Location Information
            </Typography>
            
            {selectedLocation ? (
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocationIcon fontSize="small" />
                  {selectedLocation.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedLocation.streetLine1}
                  {selectedLocation.streetLine2 && (
                    <>
                      <br />
                      {selectedLocation.streetLine2}
                    </>
                  )}
                  <br />
                  {selectedLocation.city}, {selectedLocation.province} {selectedLocation.postalCode}
                  <br />
                  {selectedLocation.country}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Button 
                    size="small" 
                    onClick={() => setLocationDialogOpen(true)}
                    sx={{ mr: 1 }}
                  >
                    Change Location
                  </Button>
                  <Button 
                    size="small" 
                    color="error"
                    onClick={() => {
                      setSelectedLocation(null);
                      locationIdInput.field.onChange(null);
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <LocationIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="body2" sx={{ mb: 2 }}>
                  No location assigned to this lead
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => setLocationDialogOpen(true)}
                >
                  Add Location
                </Button>
              </Box>
            )}

            {/* Hidden input to store the locationId */}
            <input 
              type="hidden" 
              {...locationIdInput.field}
            />
          </CardContent>
        </Card>
      </Grid>


      {/* Lead Intelligence */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PsychologyIcon color="primary" />
              Lead Intelligence
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextInput 
                  source="notes" 
                  label="Notes"
                  multiline 
                  rows={3} 
                  fullWidth 
                  helperText="General notes about this lead"
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput 
                  source="requirements" 
                  label="Requirements"
                  multiline 
                  rows={2} 
                  fullWidth 
                  helperText="What are they looking for?"
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput 
                  source="painPoints" 
                  label="Pain Points"
                  multiline 
                  rows={2} 
                  fullWidth 
                  helperText="What problems are they trying to solve?"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Location Dialog */}
      <LocationDialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        onLocationCreated={handleLocationCreated}
        title="Add Location for Lead"
      />
    </Grid>
  );
};